import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMatch, useParams } from 'react-router-dom';

import EmptyTextData from '@/components/EmptyData/EmptyTextData';
import { BaseButton } from '@/components/base-button/BaseButton';
import TableBase, { FetchColumnsType } from '@/components/common/table-base';
import { FormAttachment } from '@/components/form-attachment/FormAttachment';
import { FormCheckbox } from '@/components/form-checkbox/FormCheckbox';
import { FormSelect } from '@/components/form-select/FormSelect';

import useFetch from '@/hooks/useFetch';

import { API } from '@/utils/constants/Apis';
import { FORMAT_DATE_EN_HH_MM_SS, MAX_CAPACITY_FILE, TYPE } from '@/utils/constants/AppConstants';
import { EDIT_TASK_OF_PROJECT_URL } from '@/utils/constants/RouteContants';
import { DataViewer } from '@/utils/helpers/common';

import TrashBlack from '@/assets/icons/TrashBlack.svg';

import { DEFAULT_VALUE_TASK_REQUIRE_DOCUMENT } from '../constants';
import { IDocument, IFormAddTaskOfProject } from '../models';
import { getDocuments } from '../utils';

interface ITaskRequireDocumentsProps {
  defaultValueForm: IFormAddTaskOfProject;
}
export default function TaskRequireDocuments({ defaultValueForm }: ITaskRequireDocumentsProps) {
  const { t } = useTranslation();
  const form = useFormContext();
  const { idProject } = useParams();
  const [optionsDocumentTotal, setOptionsDocumentTotal] = useState<any[]>([]);
  const { data: optionsDocument } = useFetch<IDocument[]>(API.GET_REQUIRE_DOCUMENT_PROJECT(idProject ?? ''), 'GET');
  const modeEdit = useMatch(EDIT_TASK_OF_PROJECT_URL(':idProject', ':idTask'));
  const {
    fields: documentList,
    append,
    remove,
    update
  } = useFieldArray({
    name: 'taskRequireDocuments',
    control: form.control
  });

  useEffect(() => {
    if (defaultValueForm?.taskRequireDocuments?.length && modeEdit && optionsDocumentTotal) {
      const list = getDocuments(defaultValueForm.taskRequireDocuments);
      setOptionsDocumentTotal([...optionsDocumentTotal, ...list]);
    }
  }, [modeEdit, defaultValueForm]);

  useEffect(() => {
    if (optionsDocument) setOptionsDocumentTotal([...optionsDocumentTotal, ...optionsDocument]);
  }, [optionsDocument, modeEdit]);

  const getOptionsDocument = (indexOptions: number) => {
    const listIdDocument = form.getValues(`taskRequireDocuments`).map((item: any) => item?.projectRequiredDocumentId);
    const documentId = form.getValues(`taskRequireDocuments.${indexOptions}.projectRequiredDocumentId`);
    return optionsDocumentTotal
      ?.filter((item: any, index: number) => {
        if (documentId && item.id === documentId) return true;
        return !listIdDocument.includes(item.id);
      })
      .map((item: any) => ({ ...item, label: `${item.displayOrder}. ${item.managementCode}`, value: item.id, display: item.documentName }));
  };
  const getColumnDocument = (): FetchColumnsType<any> => [
    {
      title: t('project:task_of_project:document_code'),
      key: 'document_code',
      width: 80,
      render: (_value: any, record: any) => {
        return <div className='text-center'>{DataViewer.display(record?.displayOrder)}</div>;
      }
    },
    {
      title: t('project:task_of_project:document_name'),
      dataIndex: 'documentName',
      width: 260,
      render(_value: string, _record: any, index: number) {
        return (
          <div>
            <FormSelect
              className='w-full'
              required={true}
              options={getOptionsDocument(index)}
              optionLabelProp='display'
              name={`taskRequireDocuments.${index}.projectRequiredDocumentId`}
              placeholder={String(t('placeholder:select', { field: t('required_documents:document_name') }))}
              handleChange={(val) => {
                const document = optionsDocumentTotal?.find((item: any) => item.id === val) || DEFAULT_VALUE_TASK_REQUIRE_DOCUMENT;
                update(index, {
                  ...form.getValues(`taskRequireDocuments.${index}`),
                  ...document,
                  id: null,
                  projectRequiredDocumentId: val ?? null,
                  isConfirm: false,
                  nameConfirm: null,
                  dateConfirm: null,
                  taskRequireDocumentAttachmentFiles: []
                });
              }}
            ></FormSelect>
          </div>
        );
      }
    },
    {
      title: t('project:task_of_project:number_of_copies'),
      dataIndex: 'numberOfCopy',
      width: 60,
      render(_value: any, record: any) {
        return <span className='block max-w-full w-fit whitespace-pre-wrap break-all'>{DataViewer.display(record?.numberOfCopy)}</span>;
      }
    },
    {
      title: t('project:task_of_project:description'),
      dataIndex: 'notes',
      width: 240,
      render(_: any, record: any) {
        return <div dangerouslySetInnerHTML={{ __html: DataViewer.displayAsSanitizeHTML(record?.note) }} className='whitespace-pre-wrap'></div>;
      }
    },
    {
      title: t('project:task_of_project:attachment_file'),
      dataIndex: 'attachment_file',
      key: 'attachment_file',
      render(value: string, record: any, index: number) {
        return (
          <div>
            <FormAttachment
              name={`taskRequireDocuments.${index}.taskRequireDocumentAttachmentFiles`}
              defaultValue={form.getValues(`taskRequireDocuments.${index}.taskRequireDocumentAttachmentFiles`)}
              type='button'
              labelButton='reference_material:upload'
              sizeButton='small'
              classNameButton='max-w-[130px]'
            ></FormAttachment>
          </div>
        );
      },
      width: 220
    },
    {
      title: t('project:task_of_project:file_status'),
      dataIndex: 'status',
      width: 100,
      render(_value: any, record: any, index: number) {
        return (
          <div className='flex justify-start whitespace-nowrap'>
            <FormCheckbox
              name={`taskRequireDocuments.${index}.isConfirm`}
              handleChange={(val) => {
                update(index, {
                  ...form.getValues(`taskRequireDocuments.${index}`),
                  isConfirm: val,
                  nameConfirm: null,
                  dateConfirm: null
                });
              }}
            ></FormCheckbox>
            <p className='ml-2'>
              {t(!form.watch(`taskRequireDocuments.${index}.isConfirm`) ? 'required_documents:not_submitted' : 'required_documents:submitted')}
            </p>
          </div>
        );
      }
    },
    {
      title: t('project:task_of_project:confirmer'),
      dataIndex: 'nameConfirm',
      width: 100,
      render(_value: any, record: any) {
        const nameConfirm = () => {
          if ((!modeEdit && !record.nameConfirm) || !record.nameConfirm) return null;
          return record?.statusConfirm ? record.nameConfirm : `(${t('account_list:status_account:inactive')}) ${record.nameConfirm}`;
        };
        return (
          <span className='block max-w-full w-fit whitespace-pre-wrap break-all'>{DataViewer.display(record.isConfirm ? nameConfirm() : null)}</span>
        );
      }
    },
    {
      title: t('project:task_of_project:confirmDate'),
      dataIndex: 'dateConfirm',
      width: 100,
      render(_value: any, record: any) {
        const renderDate = () => {
          if (!modeEdit && !record.dateConfirm) return null;
          return record.isConfirm ? record.dateConfirm : null;
        };
        return <span className='whitespace-pre-wrap'>{DataViewer.localTimeBy(record.isConfirm ? renderDate() : null, FORMAT_DATE_EN_HH_MM_SS)}</span>;
      }
    },

    {
      dataIndex: 'delete',
      width: 40,
      className: '!px-0',
      align: 'center',
      render(_value: any, _record: any, index: number) {
        return (
          <div
            className='cursor-pointer'
            onClick={() => {
              remove(index);
            }}
          >
            <TrashBlack width={18} height={18} />
          </div>
        );
      }
    }
  ];
  return (
    <div>
      <TableBase
        className='w-full'
        showAddLine={false}
        columns={getColumnDocument()}
        showDownload={false}
        pagination={false}
        deleteConfirmPopup={{
          type: TYPE.DISCARD,
          title: t('common:MSG_029.1_title', { item: t('basic_information:project') }) ?? '',
          msg:
            `${t('common:MSG_029.1_description', { item: t('basic_information:project') })} ${t('common:MSG_029_description2', {
              item: t('basic_information:project')
            })}` ?? ''
        }}
        dataSource={documentList}
        rowSelection={null}
        locale={{ emptyText: <EmptyTextData className='text-center mt-4' text={'common:MSG_093'} /> }}
      />
      <BaseButton
        disabled={documentList.length >= MAX_CAPACITY_FILE}
        className='mt-4 max-w-[120px]'
        type='secondary'
        icon={<PlusOutlined />}
        onClick={() => {
          append(DEFAULT_VALUE_TASK_REQUIRE_DOCUMENT);
        }}
      >
        {t('project:task_of_project:add_document')}
      </BaseButton>
    </div>
  );
}
